import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

import "./CSS/BestSller.css";
import { API_BASE, FILE_BASE } from "../config";

const Bestseller = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =========================================================
  // SAFE VALUE
  // =========================================================
  const getSafeValue = (value, fallback = "") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "string" || typeof value === "number") {
      return value;
    }

    if (typeof value === "object") {
      return (
        value.name ||
        value.title ||
        value.productName ||
        value.value ||
        value.text ||
        fallback
      );
    }

    return fallback;
  };

  // =========================================================
  // GET PRODUCT ID
  // =========================================================
  const getProductId = (item) => {
    return item?.id || item?.productId || item?.product?.id || null;
  };

  // =========================================================
  // CALCULATE DISCOUNT
  // =========================================================
  const calculateDiscount = (originalPrice, finalPrice) => {
    const original = Number(getSafeValue(originalPrice, 0));

    const final = Number(getSafeValue(finalPrice, 0));

    if (!isNaN(original) && !isNaN(final) && original > final && original > 0) {
      return Math.round(((original - final) / original) * 100);
    }

    return 0;
  };

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(
          `${API_BASE}/Products?page=1&pageSize=100`,
        );

        console.log("Bestseller Products API Response:", response.data);

        const firstProduct = Array.isArray(response.data)
          ? response.data[0]
          : response.data?.products?.[0] || response.data?.items?.[0];

        console.log(
          "FIRST BESTSELLER PRODUCT:",
          JSON.stringify(firstProduct, null, 2),
        );

        // =====================================================
        // ARRAY
        // =====================================================
        if (Array.isArray(response.data)) {
          setProducts(response.data);
        }

        // =====================================================
        // { products: [] }
        // =====================================================
        else if (Array.isArray(response.data?.products)) {
          setProducts(response.data.products);
        }

        // =====================================================
        // { items: [] }
        // =====================================================
        else if (Array.isArray(response.data?.items)) {
          setProducts(response.data.items);
        }

        // =====================================================
        // EMPTY
        // =====================================================
        else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Error fetching bestseller products:", err);

        setProducts([]);

        setError("Unable to load products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // =========================================================
  // PRODUCT IMAGE
  // =========================================================
  const getProductImage = (image) => {
    const fallback =
      "https://dummyimage.com/400x400/e5e5e5/000000&text=No+Image";

    if (!image) {
      return fallback;
    }

    // OBJECT
    if (typeof image === "object") {
      image =
        image.imageUrl ||
        image.url ||
        image.path ||
        image.src ||
        image.image ||
        "";
    }

    if (typeof image !== "string" || !image) {
      return fallback;
    }

    // HTTPS
    if (image.startsWith("https://")) {
      return image;
    }

    // HTTP → HTTPS
    if (image.startsWith("http://")) {
      return image.replace(/^http:\/\//i, "https://");
    }

    // RELATIVE
    return `${FILE_BASE}${image.startsWith("/") ? "" : "/"}${image}`;
  };

  // =========================================================
  // GET HOVER IMAGE
  // =========================================================
  const getHoverImage = (item) => {
    if (Array.isArray(item?.images) && item.images.length > 1) {
      return item.images[1]?.imageUrl || item.images[1];
    }

    if (item?.hoverImage) {
      return item.hoverImage;
    }

    return item?.thumbnail;
  };

  // =========================================================
  // VIEW PRODUCT
  // =========================================================
  const handleProductClick = (item) => {
    const productId = getProductId(item);

    if (!productId) {
      console.error("Product ID missing:", item);

      return;
    }

    console.log("Navigating to Product:", productId);

    navigate(`/product/${productId}`);
  };

  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="bestseller-container">
      {/* HEADER */}
      <div className="bestseller-header">
        <h1>Bestseller</h1>

        <Link to="/shop" className="see-all">
          See all
        </Link>
      </div>

      {/* SUBTITLE */}
      <p className="subtitlez">
        Our most celebrated drapes, chosen time and again.
      </p>

      {/* PRODUCT GRID */}
      <div className="bestseller-product-grid">
        {/* LOADING */}
        {loading && <p>Loading products...</p>}

        {/* ERROR */}
        {!loading && error && <p>{error}</p>}

        {/* NO PRODUCTS */}
        {!loading && !error && products.length === 0 && (
          <p>No products found.</p>
        )}

        {/* PRODUCTS */}
        {!loading &&
          !error &&
          products.length > 0 &&
          products.map((item, index) => {
            const productId = getProductId(item);

            const productName = getSafeValue(item?.product, "Product");

            // =================================================
            // FINAL PRICE
            // =================================================
            const finalPrice = getSafeValue(item?.finalPrice, "");

            // =================================================
            // ORIGINAL PRICE
            // =================================================
            const originalPrice = getSafeValue(item?.originalPrice, "");

            // =================================================
            // DISCOUNT
            // =================================================
            const discount = calculateDiscount(
              item?.originalPrice,
              item?.finalPrice,
            );

            const thumbnail = item?.thumbnail;

            const hoverImage = getHoverImage(item);

            return (
              <div
                className="bestseller-card"
                key={productId || `${productName}-${index}`}
              >
                {/* =================================================
                    IMAGE
                ================================================= */}
                <div className="bestseller-image-box">
                  {/* MAIN IMAGE */}
                  <img
                    src={getProductImage(thumbnail)}
                    alt={productName}
                    className="main-img"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://dummyimage.com/400x400/e5e5e5/000000&text=No+Image";
                    }}
                  />

                  {/* =================================================
                      DISCOUNT BADGE
                      IMAGE  -10%, -20%, etc.
                  ================================================= */}
                  {discount > 0 && (
                    <span className="badges sales">-{discount}%</span>
                  )}

                  {/* HOVER IMAGE */}
                  <img
                    src={getProductImage(hoverImage)}
                    alt={productName}
                    className="hover-img"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://dummyimage.com/400x400/e5e5e5/000000&text=No+Image";
                    }}
                  />

                  {/* VIEW PRODUCT */}
                  <button
                    type="button"
                    className="buy-btn"
                    onClick={() => handleProductClick(item)}
                  >
                    View Product
                  </button>
                </div>

                {/* =================================================
                    PRODUCT NAME
                ================================================= */}
                <h3>{productName}</h3>

                {/* =================================================
                    PRICE
                ================================================= */}
                <div className="bestseller-price">
                  {/* FINAL / DISCOUNTED PRICE */}
                  {finalPrice !== "" && (
                    <span className="bestseller-new-price">
                      Rs. {finalPrice}
                    </span>
                  )}

                  {/* ORIGINAL PRICE */}
                  {Number(originalPrice) > Number(finalPrice) && (
                    <span className="bestseller-old-price">
                      Rs. {originalPrice}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default Bestseller;
